import React from "react";
import ProfileImage from "../profileImage";

const MessageItem = ({ message, isCurrentUser, type = 'private' }) => {
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else {
      return date.toLocaleDateString([], { 
        month: 'short', 
        day: 'numeric',
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  };

  // Get sender information
  const sender = message.createdBy || message.sender || message.user;
  const senderName = sender ? (sender.fullName || `${sender.firstName || ''} ${sender.lastName || ''}`.trim() || 'Unknown User') : 'Unknown User';
  const isSystemMessage = message.messageType === 'system';

  if (isSystemMessage) {
    return (
      <div className="flex justify-center my-4">
        <div className="bg-gray-100 text-gray-600 text-sm px-4 py-2 rounded-full">
          {message.message || message.content}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex ${isCurrentUser ? "justify-end" : "justify-start"} animate-fade-in`}
    >
      <div
        className={`flex max-w-[70%] ${isCurrentUser ? "flex-row-reverse" : "flex-row"}`}
      >
        {/* Profile Picture */}
        <div className="flex-shrink-0">
          <ProfileImage
            image={sender?.profilePicture}
            user={sender}
            alt={senderName}
            className={`w-8 h-8 rounded-full object-cover ${
              isCurrentUser ? "ml-3" : "mr-3"
            } ring-2 ring-white`}
          />
        </div>

        {/* Message Content */}
        <div className="flex-1 min-w-0">
          {/* Sender name for group chats */}
          {!isCurrentUser && type !== 'private' && (
            <p className="text-xs text-gray-500 mb-1 px-1">
              {senderName}
              {sender?.accountType === 'talent-scout' && (
                <span className="ml-1 text-yellow-500" title="Premium User">👑</span>
              )}
            </p>
          )}

          {/* Message bubble */}
          <div
            className={`rounded-2xl px-4 py-2 shadow-sm ${
              isCurrentUser
                ? "bg-[#F15213] text-white"
                : sender?.accountType === "talent-scout"
                ? "bg-gradient-to-r from-yellow-50 to-amber-50 text-amber-900 border border-amber-200"
                : "bg-white text-gray-800 border border-gray-100"
            }`}
          >
            <p className="text-sm leading-relaxed break-words">
              {message.message || message.content}
            </p>
            
            {/* Message metadata */}
            {message.messageType === 'announcement' && (
              <div className="mt-2 pt-2 border-t border-current border-opacity-20">
                <div className="flex items-center space-x-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <span className="text-xs font-medium">Announcement</span>
                </div>
              </div>
            )}
          </div>

          {/* Timestamp */}
          <div
            className={`text-xs text-gray-500 mt-1 px-1 ${
              isCurrentUser ? "text-right" : "text-left"
            }`}
          >
            {formatTime(message.createdAt)}
          </div>
        </div>
      </div>
    </div>
  );
};

const LoadMoreButton = ({ onLoadMore, isLoading }) => (
  <div className="flex justify-center py-4">
    <button
      onClick={onLoadMore}
      disabled={isLoading}
      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-700 text-sm rounded-full transition-colors"
    >
      {isLoading ? (
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
          <span>Loading...</span>
        </div>
      ) : (
        'Load More Messages'
      )}
    </button>
  </div>
);

const MessagesList = ({ 
  messages, 
  currentUser, 
  messagesEndRef, 
  onLoadMore,
  hasNextPage,
  isFetchingNextPage,
  type = 'private'
}) => {
  return (
    <div className="flex-1 overflow-y-auto bg-gray-50">
      <div className="p-4 space-y-4">
        {/* Load More Button at top */}
        {hasNextPage && (
          <LoadMoreButton 
            onLoadMore={onLoadMore} 
            isLoading={isFetchingNextPage} 
          />
        )}

        {/* Messages */}
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.955 8.955 0 01-2.697-.413l-3.178 1.589a1 1 0 01-1.414-1.414l1.589-3.178A8.955 8.955 0 013 12a8 8 0 018-8c4.418 0 8 3.582 8 8z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No messages yet</h3>
            <p className="text-gray-500">
              {type === 'private' 
                ? 'Start the conversation by sending a message' 
                : 'Be the first to send a message in this room'
              }
            </p>
          </div>
        ) : (
          messages.map((message) => {
            const senderId = message.createdBy?._id || message.sender?._id || message.user?._id;
            const isCurrentUser = senderId === (currentUser?._id || currentUser?.id);
            return (
              <MessageItem
                key={message._id}
                message={message}
                isCurrentUser={isCurrentUser}
                type={type}
              />
            );
          })
        )}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default MessagesList; 